import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  department?: string;
  role?: string;
  profileImage?: string;
  profile_image?: string;
};

function SettingsPage() {
  const navigate = useNavigate();

  const [profileImage, setProfileImage] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("admin");
  const [savedMessage, setSavedMessage] = useState("");

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const user: LoggedInUser = JSON.parse(storedUser);

      setUsername(user.username || "");
      setEmail(user.email || "");
      setDepartment(user.department || "");
      setRole(user.role || "admin");

      const imagePath = user.profile_image || user.profileImage || "";

      setProfileImage(
        imagePath && imagePath.startsWith("/uploads")
          ? `${imagePath}`
          : imagePath
      );
    }
  }, []);

  const handleProfileImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const storedUser = sessionStorage.getItem("loggedInUser");
    const currentUser: LoggedInUser = storedUser
      ? JSON.parse(storedUser)
      : {};

    if (!currentUser.id) {
      alert("ユーザーIDが見つかりません。再ログインしてください。");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        `/users/${currentUser.id}/profile-image`,
        {
          method: "POST",
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error("プロフィール画像のアップロードに失敗しました");
      }

      const data = await response.json();

      const fullImageUrl = `${data.profile_image}`;

      setProfileImage(fullImageUrl);

      const updatedUser: LoggedInUser = {
        ...currentUser,
        profile_image: data.profile_image,
        profileImage: fullImageUrl
      };

      sessionStorage.setItem(
        "loggedInUser",
        JSON.stringify(updatedUser)
      );

      setSavedMessage("プロフィール画像を保存しました。");

      setTimeout(() => {
        setSavedMessage("");
      }, 2500);
    } catch (error) {
      console.error("Profile image upload error:", error);
      alert("プロフィール画像のアップロードに失敗しました。");
    }
  };

  const getRoleLabel = (currentRole: string) => {
    if (currentRole === "admin") {
      return "管理者";
    }

    if (currentRole === "employee") {
      return "社員";
    }

    return currentRole || "未設定";
  };

  return (
    <div style={pageStyle}>
      <main style={mainStyle}>
        <header style={headerStyle}>
          <div>
            <h1 style={titleStyle}>設定</h1>

            <p style={subtitleStyle}>
              プロフィール情報と通知設定を確認できます
            </p>
          </div>

          <button
            onClick={() => navigate("/admin-dashboard")}
            style={backButtonStyle}
          >
            ← ダッシュボードへ戻る
          </button>
        </header>

        <section style={settingsLayoutStyle}>
          <div style={profileCardStyle}>
            <h2 style={cardTitleStyle}>プロフィール写真</h2>

            <p style={cardTextStyle}>
              プロフィール画像をアップロードできます。
            </p>

            <div style={profileImageAreaStyle}>
              <div style={profileImagePreviewStyle}>
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="プロフィール"
                    style={profileImageStyle}
                  />
                ) : (
                  <span style={profileInitialStyle}>
                    {username
                      ? username.charAt(0).toUpperCase()
                      : "A"}
                  </span>
                )}
              </div>

              <label style={uploadButtonStyle}>
                画像をアップロード

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {savedMessage && (
              <div style={successMessageStyle}>
                {savedMessage}
              </div>
            )}
          </div>

          <div style={profileCardStyle}>
            <div style={informationHeaderStyle}>
              <div>
                <h2 style={cardTitleStyle}>個人情報</h2>

                <p style={cardTextStyle}>
                  以下の情報は会社のユーザーマスタから同期されています。
                </p>
              </div>

              <span style={syncBadgeStyle}>マスターデータ</span>
            </div>

            <div style={masterDataNoticeStyle}>
              名前、メールアドレス、部署、役割はこの画面では変更できません。
              変更が必要な場合は管理者へお問い合わせください。
            </div>

            <div style={informationGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>名前</label>

                <input
                  value={username || "未設定"}
                  readOnly
                  style={readOnlyInputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>メールアドレス</label>

                <input
                  value={email || "未設定"}
                  readOnly
                  style={readOnlyInputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>部署</label>

                <input
                  value={department || "未設定"}
                  readOnly
                  style={readOnlyInputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>役割</label>

                <input
                  value={getRoleLabel(role)}
                  readOnly
                  style={readOnlyInputStyle}
                />
              </div>
            </div>
          </div>

          <div style={notificationCardStyle}>
            <div style={notificationHeaderStyle}>
              <div>
                <h2 style={cardTitleStyle}>通知設定</h2>

                <p style={cardTextStyle}>
                  LMSから送信される通知の現在の状態を確認できます。
                </p>
              </div>

              <span style={enabledBadgeStyle}>有効</span>
            </div>

            <div style={notificationInformationStyle}>
              <div style={notificationItemStyle}>
                <div style={notificationIconStyle}>🔔</div>

                <div>
                  <div style={notificationItemTitleStyle}>
                    アプリ内通知
                  </div>

                  <div style={notificationItemTextStyle}>
                    ユーザー、コース、テストに関する通知を受け取ります。
                  </div>
                </div>
              </div>

              <div style={notificationStatusStyle}>
                現在有効
              </div>
            </div>

            <div style={notificationNoteStyle}>
              通知のON・OFF切り替え機能は、通知データと連携する次の実装で追加します。
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f8fafc",
  color: "#0f172a"
};

const mainStyle: React.CSSProperties = {
  width: "100%",
  padding: "38px 32px",
  boxSizing: "border-box"
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "20px",
  marginBottom: "28px"
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 900
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#64748b",
  fontSize: "16px"
};

const backButtonStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "12px 18px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap"
};

const settingsLayoutStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "360px minmax(0, 1fr)",
  gap: "22px",
  alignItems: "start"
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)"
};

const profileCardStyle: React.CSSProperties = {
  ...cardStyle
};

const cardTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 900
};

const cardTextStyle: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#64748b",
  lineHeight: 1.7
};

const profileImageAreaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "18px",
  marginTop: "22px"
};

const profileImagePreviewStyle: React.CSSProperties = {
  width: "150px",
  height: "150px",
  borderRadius: "50%",
  backgroundColor: "#dbeafe",
  border: "4px solid #ccfbf1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden"
};

const profileImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover"
};

const profileInitialStyle: React.CSSProperties = {
  fontSize: "54px",
  fontWeight: 900,
  color: "#1e3a8a"
};

const uploadButtonStyle: React.CSSProperties = {
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "12px 18px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer"
};

const informationHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px"
};

const syncBadgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: "#ecfdf5",
  color: "#047857",
  border: "1px solid #a7f3d0",
  borderRadius: "999px",
  padding: "7px 12px",
  fontSize: "12px",
  fontWeight: 900,
  whiteSpace: "nowrap"
};

const masterDataNoticeStyle: React.CSSProperties = {
  marginTop: "20px",
  padding: "14px 16px",
  backgroundColor: "#f0fdfa",
  color: "#0f766e",
  border: "1px solid #99f6e4",
  borderRadius: "14px",
  fontSize: "13px",
  fontWeight: 700,
  lineHeight: 1.7
};

const informationGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "18px",
  marginTop: "20px"
};

const formGroupStyle: React.CSSProperties = {
  minWidth: 0
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "14px",
  fontWeight: 800,
  marginBottom: "8px",
  color: "#334155"
};

const readOnlyInputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #dbe3ef",
  borderRadius: "12px",
  padding: "13px 14px",
  fontSize: "15px",
  outline: "none",
  backgroundColor: "#f8fafc",
  color: "#334155",
  cursor: "default"
};

const successMessageStyle: React.CSSProperties = {
  marginTop: "18px",
  backgroundColor: "#dcfce7",
  color: "#166534",
  padding: "12px 14px",
  borderRadius: "12px",
  fontWeight: 800,
  textAlign: "center"
};

const notificationCardStyle: React.CSSProperties = {
  ...cardStyle,
  gridColumn: "1 / -1"
};

const notificationHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px"
};

const enabledBadgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: "#dcfce7",
  color: "#15803d",
  border: "1px solid #bbf7d0",
  borderRadius: "999px",
  padding: "7px 14px",
  fontSize: "12px",
  fontWeight: 900,
  whiteSpace: "nowrap"
};

const notificationInformationStyle: React.CSSProperties = {
  marginTop: "20px",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "20px",
  backgroundColor: "#f8fafc"
};

const notificationItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px"
};

const notificationIconStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#ccfbf1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "21px",
  flexShrink: 0
};

const notificationItemTitleStyle: React.CSSProperties = {
  color: "#0f172a",
  fontSize: "15px",
  fontWeight: 900
};

const notificationItemTextStyle: React.CSSProperties = {
  marginTop: "5px",
  color: "#64748b",
  fontSize: "13px",
  lineHeight: 1.6
};

const notificationStatusStyle: React.CSSProperties = {
  color: "#047857",
  backgroundColor: "#ecfdf5",
  border: "1px solid #a7f3d0",
  borderRadius: "12px",
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 900,
  whiteSpace: "nowrap"
};

const notificationNoteStyle: React.CSSProperties = {
  marginTop: "14px",
  color: "#64748b",
  fontSize: "12px",
  lineHeight: 1.6
};

export default SettingsPage;